import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { callsService } from '../../utils/callsService';
import Header from '../../components/ui/Header';
import NotificationCenter from '../../components/ui/NotificationCenter';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import SystemStatusIndicator from '../../components/ui/SystemStatusIndicator';
import CallSubmissionForm from './components/CallSubmissionForm';
import BiddingPreview from './components/BiddingPreview';
import SubmissionHistory from './components/SubmissionHistory';
import BulkSubmissionModal from './components/BulkSubmissionModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const CallSubmissionInterface = () => {
  const navigate = useNavigate();
  const { user, userProfile, isAuthenticated } = useAuth();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isSystemStatusOpen, setIsSystemStatusOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [currentCallData, setCurrentCallData] = useState(null);
  const [savedFormData, setSavedFormData] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [realtimeStats, setRealtimeStats] = useState({
    todaySubmissions: 0,
    activeAuctions: 0,
    avgBidPrice: 0,
    successRate: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load saved form data from localStorage
    const saved = localStorage.getItem('call_submission_draft');
    if (saved) {
      try {
        setSavedFormData(JSON.parse(saved));
      } catch (error) {
        console.error('Error loading saved form data:', error);
      }
    }

    // Load real-time stats from Supabase instead of mock data
    if (isAuthenticated && user) {
      loadRealtimeStats();
    }
    
    setLoading(false);
  }, [isAuthenticated, user]);

  const loadRealtimeStats = async () => {
    if (!user?.id) return;

    try {
      const stats = await callsService?.getPerformanceStats(user?.id, 1); // Today's stats
      setRealtimeStats({
        todaySubmissions: stats?.total_calls || 0,
        activeAuctions: stats?.in_auction_calls || 0,
        avgBidPrice: stats?.avg_price || 0,
        successRate: stats?.success_rate || 0
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleFormSubmit = async (submittedCall) => {
    try {
      // Update current call data for bidding preview
      setCurrentCallData(submittedCall);
      
      // Clear saved draft
      localStorage.removeItem('call_submission_draft');
      setSavedFormData(null);
      
      // Update stats
      setRealtimeStats(prev => ({
        ...prev,
        todaySubmissions: (prev?.todaySubmissions || 0) + 1,
        activeAuctions: (prev?.activeAuctions || 0) + 1
      }));
      
      console.log('Call submitted successfully:', submittedCall);
    } catch (error) {
      console.error('Submission error:', error);
      throw error;
    }
  };

  const handleAutoSave = (formData) => {
    localStorage.setItem('call_submission_draft', JSON.stringify(formData));
    setSavedFormData(formData);
  };

  const handleResubmit = (submissionData) => {
    setSavedFormData(submissionData);
    setCurrentCallData(null); // Clear current preview
  };

  const handleBulkSubmit = async (bulkData) => {
    try {
      // Use real Supabase service instead of mock submission
      const results = await callsService?.submitBulkCalls(bulkData);
      
      setRealtimeStats(prev => ({
        ...prev,
        todaySubmissions: (prev?.todaySubmissions || 0) + results?.length,
        activeAuctions: (prev?.activeAuctions || 0) + results?.length
      }));
      
      console.log('Bulk submission successful:', results?.length, 'calls');
    } catch (error) {
      console.error('Bulk submission error:', error);
      throw error;
    }
  };

  const handleQuickAction = (actionId, items) => {
    switch (actionId) {
      case 'submit_bulk':
        setIsBulkModalOpen(true);
        break;
      case 'export_data':
        console.log('Exporting data for items:', items);
        break;
      case 'duplicate_campaign': console.log('Duplicating campaigns:', items);
        break;
      case 'archive_items': console.log('Archiving items:', items);
        break;
      default:
        console.log('Unknown action:', actionId);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        userRole={userProfile?.role || "publisher"}
        isNotificationOpen={isNotificationOpen}
        onNotificationToggle={() => setIsNotificationOpen(!isNotificationOpen)}
      />
      <div className="pt-16">
        {/* Page Header */}
        <div className="bg-card border-b border-border">
          <div className="px-4 lg:px-6 py-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Phone" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Call Submission Interface</h1>
                  <p className="text-muted-foreground">Submit calls and track real-time bidding activity</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setIsBulkModalOpen(true)}
                  iconName="Upload"
                  iconPosition="left"
                  disabled={!isAuthenticated}
                >
                  Bulk Upload
                </Button>
                
                <Button
                  variant="default"
                  onClick={() => navigate('/publisher-dashboard')}
                  iconName="BarChart3"
                  iconPosition="left"
                >
                  View Dashboard
                </Button>
              </div>
            </div>
            
            {/* Real-time Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <Icon name="Phone" size={16} className="text-primary" />
                  <span className="text-sm text-muted-foreground">Today's Submissions</span>
                </div>
                <div className="text-2xl font-bold text-foreground">{realtimeStats?.todaySubmissions || 0}</div>
              </div>
              
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <Icon name="Activity" size={16} className="text-accent" />
                  <span className="text-sm text-muted-foreground">Active Auctions</span>
                </div>
                <div className="text-2xl font-bold text-foreground">{realtimeStats?.activeAuctions || 0}</div>
              </div>
              
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <Icon name="DollarSign" size={16} className="text-success" />
                  <span className="text-sm text-muted-foreground">Avg Bid Price</span>
                </div>
                <div className="text-2xl font-bold text-foreground">${realtimeStats?.avgBidPrice?.toFixed(2) || '0.00'}</div>
              </div>
              
              <div className="bg-muted/30 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-1">
                  <Icon name="TrendingUp" size={16} className="text-warning" />
                  <span className="text-sm text-muted-foreground">Success Rate</span>
                </div>
                <div className="text-2xl font-bold text-foreground">{realtimeStats?.successRate?.toFixed(1) || '0.0'}%</div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="px-4 lg:px-6 py-4">
          <QuickActionToolbar
            section="publisher"
            selectedItems={selectedItems}
            onAction={handleQuickAction}
            disabled={!isAuthenticated}
          />
        </div>

        {/* Main Content */}
        <div className="px-4 lg:px-6 pb-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Panel - Submission Form */}
            <div className="lg:col-span-5">
              <CallSubmissionForm
                onSubmit={handleFormSubmit}
                onAutoSave={handleAutoSave}
                savedData={savedFormData}
              />
            </div>
            
            {/* Right Panel - Bidding Preview */}
            <div className="lg:col-span-4">
              <BiddingPreview callData={currentCallData} />
            </div>
            
            {/* Far Right Panel - Additional Info */}
            <div className="lg:col-span-3">
              <div className="space-y-6">
                {/* Quick Tips */}
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon name="Lightbulb" size={16} className="text-warning" />
                    <h3 className="font-medium text-foreground">Quick Tips</h3>
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li className="flex items-start space-x-2">
                      <Icon name="ArrowRight" size={12} className="mt-0.5 flex-shrink-0" />
                      <span>Higher quality scores attract more bidders</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <Icon name="ArrowRight" size={12} className="mt-0.5 flex-shrink-0" />
                      <span>Peak hours: 9AM-5PM EST get best prices</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <Icon name="ArrowRight" size={12} className="mt-0.5 flex-shrink-0" />
                      <span>Complete demographic data improves matching</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <Icon name="ArrowRight" size={12} className="mt-0.5 flex-shrink-0" />
                      <span>Use Ctrl+Enter to submit quickly</span>
                    </li>
                  </ul>
                </div>

                {/* Market Insights */}
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon name="TrendingUp" size={16} className="text-success" />
                    <h3 className="font-medium text-foreground">Market Insights</h3>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Auto Insurance</span>
                      <span className="text-success font-medium">+12%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Home Insurance</span>
                      <span className="text-success font-medium">+8%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Life Insurance</span>
                      <span className="text-warning font-medium">-3%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Health Insurance</span>
                      <span className="text-success font-medium">+15%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Bottom Panel - Submission History */}
          <div className="mt-8">
            <SubmissionHistory onResubmit={handleResubmit} />
          </div>
        </div>
      </div>
      
      {/* Modals and Overlays */}
      <NotificationCenter
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
      />
      <SystemStatusIndicator
        isExpanded={isSystemStatusOpen}
        onToggle={() => setIsSystemStatusOpen(!isSystemStatusOpen)}
      />
      <BulkSubmissionModal
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        onSubmit={handleBulkSubmit}
      />
    </div>
  );
};

export default CallSubmissionInterface;